import { useEffect, useState } from 'react';
import { array } from 'fp-ts';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, useDialogAction, useUrlSyncedTableState } from '@mews/framework';
import {
    Dialog,
    Table,
    TableActiveColumnFilters,
    TableBulkActions,
    TableHeader,
    TableProvider,
    useFullRowSelection,
} from '@optimus-web/core';
import { PortfolioVoucherOverview, PortfolioVoucherOverviewFilters } from '@mews-commander/data-access';
import { LocalizedPageTitle, PortfolioLayout, PortfolioLayoutHeader } from '@mews-commander/ui';
import { WithGuideLink } from '@mews-commander/types';
import { VoucherEditWrapper } from '../../sections/components/vouchers/VoucherEditWrapper';
import { VoucherDeleteWrapper } from '../../sections/components/vouchers/VoucherDeleteWrapper';
import { useVoucherTableData, useDeleteVouchersMutation } from '../../../queries';
import { EditVoucherContainer } from '../../sections/components';
import { VoucherCodeWrapper } from '../../sections/components/vouchers/voucherCodes/VoucherCodeWrapper';
import { DELETE_VOUCHER_ENDING_POINT } from '../../../utilities/userFlowTestIds';
import { useColumns } from './tableMapping';
import { VoucherDetails } from './VoucherDetails/VoucherDetails';

export const VouchersTable: React.FC<WithGuideLink> = ({ guideLink }) => {
    const [voucherToDelete, setVoucherToDelete] = useState<PortfolioVoucherOverview | null>(null);
    const [voucherToEdit, setVoucherToEdit] = useState<PortfolioVoucherOverview | null>(null);
    const [selectedVoucherId, setSelectedVoucherId] = useState<string>();
    const [isVoucherDetailsPanelOpened, setIsVoucherDetailsPanelOpened] = useState<boolean>(false);

    useEffect(() => {
        setIsVoucherDetailsPanelOpened(!!selectedVoucherId);
    }, [selectedVoucherId]);

    const deleteVoucherMutation = useDeleteVouchersMutation();

    const { states, handlers } = useDialogAction(async () => Promise.resolve());

    const deleteVoucher = (voucher: PortfolioVoucherOverview) => {
        deleteVoucherMutation.mutate([voucher]);
        setVoucherToDelete(null);
        resetRowSelection();
    };

    const openEditDialog = (voucher: PortfolioVoucherOverview | null) => {
        setVoucherToEdit(voucher);
        handlers.handleOpen();
    };

    const columns = useColumns({
        setVoucherToDelete,
        setVoucherToEdit: openEditDialog,
        onNameClick: (voucher) => setSelectedVoucherId(voucher.id),
    });

    const [tableState, setTableState] = useUrlSyncedTableState<
        PortfolioVoucherOverview,
        PortfolioVoucherOverviewFilters
    >({
        columns,
        pageSize: 50,
    });

    const { data: vouchers, isFetching } = useVoucherTableData(tableState);
    const { selectedRowsData, rowSelection, setRowSelection, selectedRowsIds, hasRowsSelected, resetRowSelection } =
        useFullRowSelection(vouchers?.data);

    return (
        <PortfolioLayout>
            <PortfolioLayoutHeader
                title={<LocalizedPageTitle textKey={TextKey.VoucherPlural} />}
                guideLink={guideLink}
            />
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <TableHeader
                    title={<LocalizedText textKey={TextKey.VoucherPlural} />}
                    subTitle={vouchers?.totalRowCount || '0'}
                />
                <TableActiveColumnFilters />
                <TableBulkActions
                    disabled={!hasRowsSelected}
                    leftSide={
                        array.isNonEmpty(selectedRowsIds) && (
                            <LocalizedText
                                textKey={TextKey.SelectedItems}
                                substitutes={{ count: selectedRowsIds.length }}
                            />
                        )
                    }
                    rightSide={
                        <>
                            <VoucherCodeWrapper
                                areNoRowsSelected={!hasRowsSelected}
                                selectedVouchers={selectedRowsData}
                            />
                            <VoucherEditWrapper
                                selectedRows={selectedRowsData}
                                areNoRowsSelected={!hasRowsSelected}
                                currentPrimaryPropertyCount={selectedRowsIds.length}
                                onSuccess={resetRowSelection}
                            />
                            <VoucherDeleteWrapper selectedVouchers={selectedRowsData} onSuccess={resetRowSelection} />
                        </>
                    }
                />
                <Table
                    uniqueRowId="id"
                    data={vouchers?.data ?? []}
                    amountOfRows={vouchers?.totalRowCount ?? 0}
                    loading={isFetching}
                    rowSelection={{ rowSelection, onRowSelectionChange: setRowSelection }}
                    fixedFirstColumn
                    fixedLastColumn
                />

                {voucherToDelete && (
                    <Dialog
                        title={
                            <LocalizedText
                                textKey={TextKey.VoucherDeleteSingleTitle}
                                substitutes={{ voucherName: voucherToDelete.name }}
                            />
                        }
                        actions={[
                            {
                                children: <LocalizedText textKey={TextKey.Cancel} />,
                                onClick: () => setVoucherToDelete(null),
                                variant: 'tertiary',
                                size: 'medium',
                                type: 'button',
                            },
                            {
                                children: <LocalizedText textKey={TextKey.Delete} />,
                                onClick: () => deleteVoucher(voucherToDelete),
                                variant: 'destructive',
                                size: 'medium',
                                'data-test-id': DELETE_VOUCHER_ENDING_POINT,
                                type: 'submit',
                            },
                        ]}
                        active
                        loading={deleteVoucherMutation.isPending}
                        onClose={() => setVoucherToDelete(null)}
                    >
                        <LocalizedText textKey={TextKey.VoucherDeleteSingleDescription} />
                    </Dialog>
                )}

                {voucherToEdit && (
                    <EditVoucherContainer
                        states={states}
                        handlers={handlers}
                        selectedVouchers={[voucherToEdit]}
                        selectedVouchersCount={1}
                        onSuccess={handlers.handleConfirm}
                    />
                )}
            </TableProvider>
            <VoucherDetails
                key={selectedVoucherId}
                voucher={vouchers?.data?.find((voucher) => voucher.id === selectedVoucherId)}
                isOpened={isVoucherDetailsPanelOpened}
                onClose={() => setIsVoucherDetailsPanelOpened(false)}
                onUnmount={() => setSelectedVoucherId(undefined)}
            />
        </PortfolioLayout>
    );
};
